import { useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Check, X, Minus, Send, Loader2, MessageSquareOff, MessageSquare, Link2 } from "lucide-react";
import { patchStrategySlot, strategySlotToPlan } from "@/lib/api";
import { toast } from "sonner";

const STATI = ["vuoto", "proposto", "approvato", "scritto", "pubblicato"];
const STATO_COLOR = { vuoto: "#525252", proposto: "#D97706", approvato: "#2563EB", scritto: "#7C3AED", pubblicato: "#16A34A" };
const CONTROLLI = [["novita", "Novità"], ["specificita", "Specificità"], ["share", "Share"], ["emozione", "Emozione"], ["frase", "Frase"]];

function Controllo({ ok, label }) {
  const Icon = ok === true ? Check : ok === false ? X : Minus;
  const color = ok === true ? "#16A34A" : ok === false ? "#D92D20" : "#404040";
  return <span title={label} className="inline-flex items-center gap-0.5 text-[0.62rem]" style={{ color }}><Icon size={11} />{label}</span>;
}

function SlotCard({ slot, weekStart, onChange }) {
  const [busy, setBusy] = useState(false);
  const isCta = slot.cta && slot.cta !== "nessuna";
  const empty = slot.stato === "vuoto" && !slot.titolo;
  const setStato = async (stato) => {
    setBusy(true);
    try { onChange(await patchStrategySlot(weekStart, slot.giorno, { stato })); } catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const toPlan = async () => {
    setBusy(true);
    try { onChange(await strategySlotToPlan(weekStart, slot.giorno)); toast.success("Mandato al Calendario"); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-3 ${empty ? "border-dashed border-[#262626] bg-transparent" : "border-[#262626] bg-[#0d0d0d]"}`} data-testid={`strategy-slot-${slot.giorno}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-bold text-white">{slot.giorno_label} <span className="text-neutral-500 font-normal">{slot.data.slice(8)}/{slot.data.slice(5, 7)}</span></p>
          {slot.format_fisso && <p className="text-[0.62rem] uppercase tracking-wide mt-0.5" style={{ color: "#D97706" }}>{slot.format_fisso.split(" — ")[0]}</p>}
        </div>
        <span className="text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full border" style={{ color: STATO_COLOR[slot.stato], borderColor: STATO_COLOR[slot.stato] }} data-testid={`strategy-slot-${slot.giorno}-stato`}>{slot.stato}</span>
      </div>

      {empty ? (
        <div className="text-xs text-neutral-600 leading-snug">
          <p>Da proporre venerdì.</p>
          {slot.plan_hint && <p className="mt-2 text-neutral-500">Nel Calendario: <span className="text-neutral-300">{slot.plan_hint.titolo}</span> <span className="uppercase text-[0.6rem]">({slot.plan_hint.stato})</span></p>}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {slot.format_mik && <span className="text-[0.62rem] px-2 py-0.5 rounded bg-[#1a1a1a] text-neutral-300 border border-[#262626]">{slot.format_mik}</span>}
            {slot.frame && <span className="text-[0.62rem] px-2 py-0.5 rounded bg-[#1a1a1a] text-neutral-300 border border-[#262626]">Frame {slot.frame}</span>}
            <span className="text-[0.62rem] px-2 py-0.5 rounded border inline-flex items-center gap-1" style={{ color: isCta ? "#16A34A" : "#737373", borderColor: isCta ? "#16A34A55" : "#262626" }}>{isCta ? <MessageSquare size={10} /> : <MessageSquareOff size={10} />}{isCta ? `CTA ${slot.cta}` : "senza CTA"}</span>
            {slot.link_edits && slot.link_edits !== "nessuno" && (
              <span className="text-[0.62rem] px-2 py-0.5 rounded border inline-flex items-center gap-1" style={{ color: "#38BDF8", borderColor: "#38BDF855" }} data-testid={`strategy-slot-${slot.giorno}-link-edits`}><Link2 size={10} />Edits → {slot.link_edits}</span>
            )}
          </div>
          <p className="text-sm font-semibold text-white leading-snug" data-testid={`strategy-slot-${slot.giorno}-titolo`}>{slot.titolo}</p>
          {slot.hook && <p className="text-[0.7rem] font-mono uppercase text-neutral-300 leading-snug border-l-2 pl-2" style={{ borderColor: "#D97706" }}>{slot.hook}</p>}
          {slot.fatto_nemico && <p className="text-xs text-neutral-400 leading-snug"><span className="text-neutral-500">Nemico-fatto:</span> {slot.fatto_nemico}</p>}
          {slot.emozione && <p className="text-xs text-neutral-400"><span className="text-neutral-500">Emozione:</span> {slot.emozione}</p>}
          {slot.frase_inoltrabile && <p className="text-xs italic text-neutral-300 leading-snug">«{slot.frase_inoltrabile}»</p>}
          <div className="flex flex-wrap gap-2" data-testid={`strategy-slot-${slot.giorno}-controlli`}>
            {CONTROLLI.map(([k, l]) => <Controllo key={k} ok={slot.controlli?.[k]} label={l} />)}
          </div>
          {slot.note && <p className="text-[0.68rem] text-neutral-500 leading-snug">{slot.note}</p>}
        </>
      )}

      {!empty && (
        <div className="mt-auto flex items-center gap-2 pt-1">
          <select value={slot.stato} onChange={(e) => setStato(e.target.value)} disabled={busy} data-testid={`strategy-slot-${slot.giorno}-stato-select`} className="bg-black/40 border border-[#262626] rounded-md text-xs text-neutral-300 px-2 py-1">
            {STATI.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {!slot.plan_item_id && !slot.plan_hint && (
            <button onClick={toPlan} disabled={busy} data-testid={`strategy-slot-${slot.giorno}-to-plan`} className="ml-auto inline-flex items-center gap-1 text-xs text-neutral-300 hover:text-white border border-[#262626] hover:border-[#404040] rounded-md px-2 py-1 transition-colors">{busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Calendario</button>
          )}
          {(slot.plan_item_id || slot.plan_hint) && <span className="ml-auto text-[0.62rem] text-neutral-600">nel Calendario</span>}
        </div>
      )}
    </div>
  );
}

export function WeekPlanPanel({ week, onNavigate, onChange }) {
  if (!week) return null;
  const ws = week.week_start;
  const label = `${ws.slice(8)}/${ws.slice(5, 7)} → ${week.slots[6].data.slice(8)}/${week.slots[6].data.slice(5, 7)}`;
  const proposti = week.slots.filter((s) => s.stato !== "vuoto").length;
  return (
    <section className="border border-[#262626] rounded-xl bg-[#111111] p-6 space-y-5" data-testid="strategy-week-plan">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2"><CalendarDays size={18} style={{ color: "#D97706" }} /> Piano della settimana</h2>
          <p className="text-xs text-neutral-500 mt-1">7 contenuti, ognuno con format Mik, frame, nemico-fatto e i 5 controlli. {proposti}/7 pianificati.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onNavigate(week.prev)} data-testid="strategy-week-prev" className="p-1.5 border border-[#262626] rounded-md text-neutral-400 hover:text-white"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-white min-w-[130px] text-center" data-testid="strategy-week-label">{label}</span>
          <button onClick={() => onNavigate(week.next)} data-testid="strategy-week-next" className="p-1.5 border border-[#262626] rounded-md text-neutral-400 hover:text-white"><ChevronRight size={16} /></button>
          <button onClick={() => onNavigate(null)} data-testid="strategy-week-today" className="text-xs text-neutral-400 hover:text-white border border-[#262626] rounded-md px-2 py-1.5">Oggi</button>
        </div>
      </div>
      {week.note && <p className="text-xs text-neutral-400 rounded-lg border border-[#262626] p-3 leading-relaxed" data-testid="strategy-week-note">{week.note}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {week.slots.map((s) => <SlotCard key={s.giorno} slot={s} weekStart={ws} onChange={onChange} />)}
      </div>
    </section>
  );
}
